import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Loader2, Filter, X, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { useAuth } from '../../contexts/AuthContext';
import {
  scheduledDashboardAPI,
  type ScheduledDashboardData,
} from '../../lib/api/scheduled-dashboard';
import { coreAPI } from '../../lib/api';
import { CurrentWeek } from './scheduled/CurrentWeek';
import { PerWeek } from './scheduled/PerWeek';
import { YTD } from './scheduled/YTD';
import { Forecast } from './scheduled/Forecast';
import DemandStability from './scheduled/DemandStability';
import { PartInfoBar } from './scheduled/PartInfoBar';
import SearchableSelect from '../ui/SearchableSelect';

type SubTab = 'current_week' | 'per_week' | 'ytd' | 'forecast' | 'stability';

const ALL_SUB_TABS: Array<{ id: SubTab; label: string; sub: string }> = [
  { id: 'current_week', label: 'Current Week', sub: 'this week' },
  { id: 'per_week',     label: 'Per Week',     sub: '12-week trend' },
  { id: 'ytd',          label: 'Year to Date', sub: 'Jan 1 → today' },
  { id: 'forecast',     label: 'Forecast',     sub: 'today → ∞' },
  { id: 'stability',    label: 'Demand Stability', sub: 'wk-over-wk churn' },
];

interface SupplierOption { id: number; name: string }

/** Format a Mon–Sun ISO week range as e.g. "12 – 18 May 2026" /
 *  "28 Apr – 4 May 2026". Dates are treated as UTC to match the
 *  backend's date-only window bounds. */
function formatWeekRange(startIso?: string, endIso?: string): string {
  if (!startIso || !endIso) return '';
  const s = new Date(startIso + 'T00:00:00Z');
  const e = new Date(endIso + 'T00:00:00Z');
  const sameMonth = s.getUTCFullYear() === e.getUTCFullYear() && s.getUTCMonth() === e.getUTCMonth();
  const opts = (withMonth: boolean, withYear: boolean): Intl.DateTimeFormatOptions => ({
    day: 'numeric',
    ...(withMonth ? { month: 'short' } : {}),
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
  const left = s.toLocaleDateString('en-GB', opts(!sameMonth, false));
  const right = e.toLocaleDateString('en-GB', opts(true, true));
  return `${left} – ${right}`;
}

/** Short relative hint for the selected week (0 = live week). */
function weekRelativeLabel(offset: number): string {
  if (offset === 0) return 'This week';
  if (offset === -1) return 'Last week';
  return `${Math.abs(offset)} weeks ago`;
}

export interface ScheduledShellProps {
  /** ERP order type to scope all data: '4' = scheduling agreements, '1' = spot orders. */
  erpOrderType: '4' | '1';
  /** Visible scope statement shown above the sub-tabs. */
  scopeNotice: string;
  /**
   * Whether to expose the Forecast sub-tab. Forecast is meaningful for
   * scheduling agreements (firm window per part); spot/one-off orders
   * have no firm window so the tab is hidden by default for them.
   */
  showForecast?: boolean;
  /** Loading message shown while the dashboard payload is in flight. */
  loadingLabel?: string;
  /** Changes when the Dashboard's Refresh is pressed: reload, past the server cache. */
  refreshStamp?: number;
}

/**
 * Shared shell for both the Scheduled Dashboards (ERP type 4) and Spot Orders
 * (ERP type 1) tabs. Owns: filter state, suppliers fetch, dashboard fetch,
 * sub-tab control, scope notice, and dispatch into period sub-components.
 */
export function ScheduledShell({
  erpOrderType,
  scopeNotice,
  showForecast = true,
  loadingLabel = 'Loading KPIs…',
  refreshStamp = 0,
}: ScheduledShellProps) {
  const servedRefreshStamp = useRef(refreshStamp);
  const { selectedCompanyId, viewKey } = useView();
  const { isSupplier: portalIsSupplier } = usePortalMode();
  const { user } = useAuth();
  const { formatCurrency } = useFormatters();

  // For supplier-portal users, `selectedCompanyId` is the supplier's own
  // company — the wrong axis for /analytics/scheduled-dashboard/, which
  // requires the CUSTOMER they serve. Resolve via the customer-filter list
  // (the same source the Dashboard customer dropdown uses). Customer-portal
  // and Helix users continue to send `selectedCompanyId` as the customer.
  // `undefined` = still resolving, `null` = resolved to nothing. Keeping these
  // distinct matters: the fetch effect below waits on the unresolved state, so
  // if a supplier with no POs (empty customer list, or a failed lookup) left
  // this at the initial value forever, the fetch would never fire and the tab
  // would spin indefinitely with nothing in flight to end it.
  const [supplierCustomerId, setSupplierCustomerId] = useState<number | null | undefined>(undefined);
  useEffect(() => {
    if (!portalIsSupplier) { setSupplierCustomerId(null); return; }
    let cancelled = false;
    setSupplierCustomerId(undefined);
    (async () => {
      try {
        const resp = await coreAPI.getAccessibleCompanies({ context: 'customer_filter' });
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: Array<{ id: number }> =
          Array.isArray(raw) ? (raw as Array<{ id: number }>)
          : Array.isArray((raw as { results?: unknown }).results) ? (raw as { results: Array<{ id: number }> }).results
          : [];
        // Always settle on a definite value so the fetch effect can proceed.
        setSupplierCustomerId(results.length > 0 ? results[0]!.id : null);
      } catch {
        if (!cancelled) setSupplierCustomerId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [portalIsSupplier]);

  const [data, setData] = useState<ScheduledDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Benign "nothing to show yet" state, kept separate from `error` so it can
  // render as an empty state rather than as a failure.
  const [noCustomer, setNoCustomer] = useState(false);
  const [activeTab, setActiveTab] = useState<SubTab>('current_week');
  // Current Week selector: 0 = live week, -1 = last week, … Only the
  // current_week block of the payload moves; YTD/Per-Week stay anchored to
  // real today. Kept across filter changes so the user can compare the same
  // week across suppliers/parts.
  const [weekOffset, setWeekOffset] = useState(0);

  // Demand Stability is a customer-side planning view — supplier-portal
  // users never see it, whatever the shell variant.
  const subTabs = useMemo(
    () => ALL_SUB_TABS.filter((t) => {
      if (!showForecast && (t.id === 'forecast' || t.id === 'stability')) return false;
      if (portalIsSupplier && t.id === 'stability') return false;
      return true;
    }),
    [showForecast, portalIsSupplier],
  );

  // Filter state — committed values are what's sent to the API. The part
  // input auto-commits 400ms after the user stops typing, so they don't have
  // to remember to press Enter. Enter still commits immediately; Esc clears.
  const [supplierId, setSupplierId] = useState<string>('');
  const [partInput, setPartInput] = useState<string>('');
  const [partCommitted, setPartCommitted] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setPartCommitted(partInput.trim()), 400);
    return () => clearTimeout(t);
  }, [partInput]);

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);

  // Use the portal-aware /for-filter/ action — the default /core/companies/
  // list is gated by IsHelixUser, so a Customer Admin (e.g. Dennis Eagle)
  // gets 403 and the dropdown silently stays empty. `for-filter` returns the
  // user's visible suppliers (customer portal → suppliers they trade with;
  // helix → all active; supplier portal → own group). Returns the full set
  // in one response so no pagination loop is needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await coreAPI.getSupplierSitesForFilter();
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: Array<{ id: number; name: string }> =
          Array.isArray(raw) ? (raw as Array<{ id: number; name: string }>)
          : Array.isArray((raw as { results?: unknown }).results) ? (raw as { results: Array<{ id: number; name: string }> }).results
          : [];
        setSuppliers(
          results
            .map((c) => ({ id: c.id, name: c.name || `Supplier ${c.id}` }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch { /* dropdown stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [viewKey]); // supplier options follow the site switcher

  // Dashboard fetch — refires when scope or filters change.
  useEffect(() => {
    // Supplier-portal users need a resolved customer to authorise the call;
    // don't fire the fetch until the customer-filter list lands.
    if (portalIsSupplier && supplierCustomerId === undefined) { setLoading(true); return; }
    // Resolution finished and found no customer — the call can never be
    // authorised, so show the empty state rather than spinning on a request
    // that will never be made. Not an `error`: nothing has gone wrong, the
    // company simply has no orders against it yet.
    if (portalIsSupplier && supplierCustomerId === null) {
      setLoading(false);
      setError(null);
      setNoCustomer(true);
      return;
    }
    setNoCustomer(false);
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: Record<string, string | number> = { erp_order_type: erpOrderType };
    const customerId = portalIsSupplier ? supplierCustomerId : (selectedCompanyId ? Number(selectedCompanyId) : null);
    if (customerId) params['customer_id'] = customerId;
    if (supplierId) params['supplier_id'] = Number(supplierId);
    if (partCommitted) params['part_number'] = partCommitted;
    if (weekOffset !== 0) params['week_offset'] = weekOffset;
    if (refreshStamp !== servedRefreshStamp.current) params['fresh'] = refreshStamp;
    servedRefreshStamp.current = refreshStamp;

    scheduledDashboardAPI
      .get(params)
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load dashboard';
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCompanyId, portalIsSupplier, supplierCustomerId, erpOrderType, supplierId, partCommitted, weekOffset, refreshStamp]);

  // If Forecast was the active tab and the shell is reused for Spot Orders
  // (or showForecast flips off), drop back to Current Week.
  useEffect(() => {
    if (!showForecast && (activeTab === 'forecast' || activeTab === 'stability')) setActiveTab('current_week');
    if (portalIsSupplier && activeTab === 'stability') setActiveTab('current_week');
  }, [showForecast, portalIsSupplier, activeTab]);

  const activeFilters = useMemo(() => {
    const labels: string[] = [];
    if (supplierId) {
      const name = suppliers.find((s) => String(s.id) === supplierId)?.name ?? `Supplier ${supplierId}`;
      labels.push(`Supplier: ${name}`);
    }
    if (partCommitted) labels.push(`Part or supplier part: ${partCommitted}`);
    return labels;
  }, [supplierId, partCommitted, suppliers]);

  const clearFilters = () => {
    setSupplierId('');
    setPartInput('');
    setPartCommitted('');
  };

  // Filter bar always renders so the user can adjust scope even on error.
  const filterBar = (
    <div
      className="rounded-lg border border-slate-200 bg-white px-4 py-3"
      role="group"
      aria-label="Dashboard filters"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <Filter className="h-3.5 w-3.5" />
          <span>Filters</span>
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="sd-supplier" className="text-xs text-slate-500">Supplier</label>
          {portalIsSupplier ? (
            // Supplier portal is always locked to the user's own company —
            // show the name rather than a picker implying a wider choice.
            <div
              className="w-96 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700"
              title="Your view is always scoped to your own company"
            >
              {user?.companyName || suppliers[0]?.name || 'Your company'}
            </div>
          ) : (
            <div className="w-96">
              <SearchableSelect
                options={suppliers.map((s) => ({ value: String(s.id), label: s.name }))}
                value={supplierId}
                onChange={setSupplierId}
                placeholder="All suppliers"
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <label htmlFor="sd-part" className="text-xs text-slate-500">Part</label>
          <input
            id="sd-part"
            type="text"
            value={partInput}
            onChange={(e) => setPartInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPartCommitted(partInput.trim());
              if (e.key === 'Escape') { setPartInput(''); setPartCommitted(''); }
            }}
            placeholder="Part or supplier part number"
            title="Matches our part number or the supplier's own part number. Auto-applies after you stop typing. Esc to clear."
            className="h-8 w-56 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
          />
        </div>

        {activeFilters.length > 0 && (
          <button
            onClick={clearFilters}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400"
          >
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span>{scopeNotice}</span>
        {activeFilters.length > 0 && (
          <>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600">Filtered by {activeFilters.join(' · ')}</span>
          </>
        )}
      </div>
    </div>
  );

  // Full loader only on the initial load (no data yet). Subsequent refetches
  // (filter or week-selector changes) keep the existing panels visible and
  // show a lightweight busy hint, so scrubbing weeks doesn't blank the page.
  if (loading && !data) {
    return (
      <div className="space-y-6">
        {filterBar}
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{loadingLabel}</span>
        </div>
      </div>
    );
  }

  if (noCustomer) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No scheduled orders yet</p>
            <p className="max-w-sm text-sm text-muted-foreground">
              This dashboard fills in once your customer raises scheduling-agreement
              orders against your company.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {filterBar}
        <Card>
          <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {filterBar}

      <div className="border-b border-slate-200" role="tablist" aria-label="Time period">
        <div className="flex gap-1">
          {subTabs.map((t) => {
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 text-sm relative transition-colors ${
                  active ? 'text-slate-900 font-semibold' : 'text-slate-500 font-medium hover:text-slate-800'
                }`}
              >
                <span>{t.label}</span>
                <span className="ml-2 text-[11px] font-normal text-slate-400">{t.sub}</span>
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-slate-900" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'current_week' && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setWeekOffset((o) => o - 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
              title="Previous week"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2 px-1">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-semibold text-slate-800 tabular-nums">
                {formatWeekRange(data.current_week.window_start, data.current_week.window_end)}
              </span>
              <span className="text-[11px] font-medium text-slate-400">{weekRelativeLabel(weekOffset)}</span>
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            </div>
            <button
              type="button"
              onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
              disabled={weekOffset >= 0}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Next week"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {weekOffset !== 0 && (
            <button
              type="button"
              onClick={() => setWeekOffset(0)}
              className="text-xs font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline"
            >
              Jump to this week
            </button>
          )}
        </div>
      )}

      {/* Part identity — rendered outside the sub-tab switch so the current
          supplier and unit price stay on screen for every period view.
          Self-hides when no single part is searched. */}
      {(data?.part_info || data?.supplier_info) && (
        <div className="mb-4">
          <PartInfoBar info={data.part_info} supplierInfo={data.supplier_info} />
        </div>
      )}

      <div>
        {(() => {
          // Customer scope: prefer the user's explicit selection, then fall
          // back to the customer the dashboard backend resolved to (the
          // helix-admin "first customer" auto-pick when no override). Without
          // this fallback, drilldown modals send no customer filter and
          // their counts include rows the tile excluded — breaking the
          // tile = drilldown contract.
          //
          // For SUPPLIER-portal users `selectedCompanyId` is the supplier's
          // OWN company — never a customer — so using it here makes the
          // drilldown send `po_line__po__customer=<supplier id>`, which
          // matches no POs and the modal comes back empty even though the
          // tile/chart counted rows. Mirror the dashboard fetch above and use
          // the resolved `supplierCustomerId` (the customer they serve).
          const resolvedCustomerId = portalIsSupplier
            ? (supplierCustomerId ?? data.customer_id ?? undefined)
            : (selectedCompanyId ?? data.customer_id ?? undefined);
          const sharedFilters = {
            customer_id: resolvedCustomerId,
            supplier_id: supplierId || undefined,
            part_number: partCommitted || undefined,
            erp_order_type: erpOrderType,
          };
          if (activeTab === 'current_week') return <CurrentWeek data={data} formatCurrency={formatCurrency} filters={sharedFilters} />;
          if (activeTab === 'per_week')     return <PerWeek     data={data} formatCurrency={formatCurrency} filters={sharedFilters} />;
          if (activeTab === 'ytd')          return <YTD         data={data} formatCurrency={formatCurrency} filters={sharedFilters} />;
          if (activeTab === 'forecast' && showForecast) return <Forecast data={data} formatCurrency={formatCurrency} filters={sharedFilters} />;
          if (activeTab === 'stability' && showForecast && !portalIsSupplier) return <DemandStability filters={sharedFilters} />;
          return null;
        })()}
      </div>
    </div>
  );
}

/** Scheduling-agreement view (ERP type 4). */
export function ScheduledTab({ refreshStamp }: Pick<ScheduledShellProps, 'refreshStamp'>) {
  return (
    <ScheduledShell
      refreshStamp={refreshStamp}
      erpOrderType="4"
      scopeNotice="Showing scheduling-agreement orders only. Spot / one-off orders are on the Spot Orders tab."
      loadingLabel="Loading scheduled KPIs…"
      showForecast
    />
  );
}
